// src/prototype/pages/StrategyIntelligenceBriefPage.tsx

import { StrategyBriefHero } from '@/components/snapshot/strategy-brief/StrategyBriefHero';
import { StrategyBriefScorecard } from '@/components/snapshot/strategy-brief/StrategyBriefScorecard';
import { StrategyBriefQuadrant } from '@/components/snapshot/strategy-brief/StrategyBriefQuadrant';
import { StrategyBriefOpportunityCard } from '@/components/snapshot/strategy-brief/StrategyBriefOpportunityCard';
import { StrategyBriefCTA } from '@/components/snapshot/strategy-brief/StrategyBriefCTA';

import { strategyIntelligenceBriefMock } from '@/mockData/strategyIntelligenceBriefMock';
import { usePageContext } from '@/contexts/PageContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FileDown, Lock } from 'lucide-react';

export default function StrategyIntelligenceBriefPage() {
  const data = strategyIntelligenceBriefMock;

  const { setPageContext } = usePageContext();
  const exportRootRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    const el = exportRootRef.current;
    if (!el) return;

    setExporting(true);
    try {
      const mod = await import('html2pdf.js');
      const html2pdf = (mod as any).default ?? (mod as any);

      const filename = `Strategy-Intelligence-Brief_${new Date().toISOString().slice(0, 10)}.pdf`;
      await html2pdf()
        .set({
          margin: [0.4, 0.4, 0.55, 0.4],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: '#FFFFFF',
          },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .from(el)
        .save();
    } finally {
      setExporting(false);
    }
  };

  const rightSlot = useMemo(
    () => (
      <button
        type="button"
        onClick={handleExport}
        disabled={exporting}
        className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-sm font-semibold text-[#18365E] shadow-sm hover:bg-black/5 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <FileDown className="h-4 w-4" />
        {exporting ? 'Exporting…' : 'Export'}
      </button>
    ),
    [exporting]
  );

  useEffect(() => {
    setPageContext({
      title: 'Strategy Intelligence Brief',
      breadcrumbs: [{ label: 'Home', href: '/home' }, { label: 'Strategy Intelligence Brief' }],
      tabs: [],
      rightSlot,
    });

    return () => {
      setPageContext({ rightSlot: undefined, breadcrumbs: undefined, title: undefined });
    };
  }, [rightSlot, setPageContext]);

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-8">
      <div ref={exportRootRef} className="pt-6">

        {/* Hero — premium client-specific header */}
        <StrategyBriefHero
          title={data.title}
          subtitle={data.subtitle}
          executiveSummary={data.executiveSummary}
          clientName={data.clientName}
          preparedDate={data.preparedDate}
        />

        {/* Opportunity Signal — answers "why does this matter" */}
        <div className="mt-3 rounded-2xl border-l-4 border-l-[#FF6E4C] border border-[#E5E7EB] bg-white shadow-sm px-5 py-4 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#FF6E4C]">
            Opportunity Signal
          </div>
          <p className="mt-1.5 text-sm font-semibold text-[#18365E] leading-snug">
            {data.opportunitySignal.statement}
          </p>
          <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.opportunitySignal.signals.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-3.5 py-2.5"
              >
                <div className="text-[11px] font-extrabold text-[#18365E]">{s.label}</div>
                <div className="text-[11px] text-[#717182] leading-snug mt-0.5">{s.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scorecard */}
        <div className="mt-3 break-inside-avoid">
          <StrategyBriefScorecard
            score={data.score}
            dimensions={data.dimensions}
            interpretation={data.scoreInterpretation}
          />
        </div>

        {/* Quadrant */}
        <div className="mt-3 break-inside-avoid">
          <StrategyBriefQuadrant
            xLabel={data.quadrant.xLabel}
            yLabel={data.quadrant.yLabel}
            points={data.quadrant.points}
            keyInsight={data.quadrant.keyInsight}
          />
        </div>

        {/* How to Interpret This Brief — placed after quadrant, top of page 2 */}
        <div className="mt-3 rounded-2xl border border-[#E5E7EB] bg-white shadow-sm px-5 py-4 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
            Reading guide
          </div>
          <h2 className="text-base font-extrabold text-[#18365E] tracking-tight mt-1">
            How to Interpret This Brief
          </h2>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">

            {/* What This Shows */}
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3">
              <div className="text-[11px] font-extrabold text-[#18365E] uppercase tracking-wide">
                What This Shows
              </div>
              <ul className="mt-2 space-y-1.5">
                {data.interpretationGuide.shows.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#FF6E4C] flex-shrink-0" />
                    <span className="text-[11px] text-[#18365E] leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* How to Use It */}
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3">
              <div className="text-[11px] font-extrabold text-[#18365E] uppercase tracking-wide">
                How to Use It
              </div>
              <ul className="mt-2 space-y-1.5">
                {data.interpretationGuide.howToUse.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#18365E] flex-shrink-0" />
                    <span className="text-[11px] text-[#18365E] leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Intended Audience */}
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3">
              <div className="text-[11px] font-extrabold text-[#18365E] uppercase tracking-wide">
                Intended Audience
              </div>
              <div className="mt-2 space-y-2">
                {data.interpretationGuide.audience.map((a) => (
                  <div key={a.role} className="flex items-start gap-2.5">
                    <div className="h-5 w-8 rounded-md bg-[#18365E] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[9px] font-extrabold text-white tracking-wide">{a.role}</span>
                    </div>
                    <span className="text-[11px] text-[#717182] leading-snug">{a.useCase}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Recognition Moment — key turning-point insight */}
        <div className="mt-3 rounded-2xl border-l-4 border-l-[#FF6E4C] border border-[#18365E]/15 bg-gradient-to-br from-white to-[#18365E]/6 shadow-sm px-5 py-4 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
            Recognition Moment
          </div>
          <p className="mt-2 text-[17px] text-[#18365E] leading-snug font-extrabold italic max-w-4xl">
            "{data.recognitionMoment}"
          </p>
        </div>

        {/* Opportunities */}
        <div className="mt-3 rounded-2xl border border-[#E5E7EB] bg-white shadow-sm p-5 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
            Opportunities
          </div>
          <h2 className="text-base font-extrabold text-[#18365E] tracking-tight mt-1">
            {data.opportunities.title}
          </h2>

          <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
            {data.opportunities.items.map((o) => (
              <StrategyBriefOpportunityCard
                key={o.id}
                title={o.title}
                systemInsight={o.systemInsight}
                implication={o.implication}
                opportunity={o.opportunity}
              />
            ))}
          </div>
        </div>

        {/* What This Means */}
        <div className="mt-2.5 rounded-2xl border border-[#E5E7EB] bg-white shadow-sm px-5 py-3.5 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
            What this means
          </div>
          <p className="text-sm font-semibold text-[#18365E] mt-1.5 leading-relaxed">
            {data.interpretation}
          </p>
        </div>

        {/* Conversion Bridge */}
        <div className="mt-2.5 rounded-2xl border border-[#E5E7EB] bg-white shadow-sm px-5 py-3.5 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
            What's next
          </div>
          <h2 className="text-base font-extrabold text-[#18365E] tracking-tight mt-1">
            {data.notIncluded.title}
          </h2>

          <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.notIncluded.items.map((item) => (
              <div
                key={item}
                className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-2.5 text-[12px] font-semibold text-[#18365E]"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Future State Preview */}
        <div className="mt-2.5 rounded-2xl border border-[#E5E7EB] bg-white shadow-sm px-5 py-3.5 break-inside-avoid">
          <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
            Future state preview
          </div>
          <h2 className="text-base font-extrabold text-[#18365E] tracking-tight mt-1">
            {data.futurePreview.title}
          </h2>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.futurePreview.blocks.map((b) => (
              <div
                key={b.id}
                className="relative rounded-2xl border border-[#E5E7EB] bg-[#F5F7FA] p-4 overflow-hidden break-inside-avoid"
              >
                <div className="absolute inset-0 bg-white/30" />
                <div className="absolute right-3 top-3 h-7 w-7 rounded-xl bg-white/80 border border-[#E5E7EB] flex items-center justify-center">
                  <Lock className="h-3.5 w-3.5 text-[#18365E]" />
                </div>
                <div className="relative">
                  <div className="text-sm font-extrabold text-[#18365E] leading-snug pr-8">
                    {b.title}
                  </div>
                  <div className="text-[11px] text-[#717182] leading-relaxed mt-1.5">
                    {b.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-3 break-inside-avoid">
          <StrategyBriefCTA
            headline={data.cta.headline}
            supportingCopy={data.cta.supportingCopy}
            buttonLabel={data.cta.buttonLabel}
          />
        </div>

      </div>
    </main>
  );
}
